import { useQuery, useMutation } from '@apollo/client'
import { abandonedCart, mergeAbandonedCarts } from 'services/abandonedCart'
import { useCart } from 'lib/core/hooks/useCart'

import { produce } from 'immer'
import { EMPTY_STRING, DEFAULT_CURRENCY_CODE } from 'lib/core/resources/constants'
import {
  MagentoShippingCartAddress,
  MagentoBillingCartAddress,
  MagentoAvailableShippingMethod,
  MagentoSelectedShippingMethod,
  MagentoStoreConfig,
  AvailableShippingMethodProcessor,
  SelectedShippingMethodProcessor, MagentoCart, CartId
} from 'lib/types'
import { getSlice as getStoreConfigSlice } from 'lib/core/data/common/storeConfig/selectors'
import { normalizeProduct } from 'lib/modules/catalog'
import { getProductAttributeList } from 'lib/core/data/common/productAttributeList/selectors'
import { ProductAttributeList } from 'lib/core/data/common/productAttributeList/types'

import {
  CartProcessors,
  CartObject,
  CartSlice,
  CartData,
  BillingAddress,
  ShippingAddress,
  CartItem
} from 'lib/core/data/quote/cart/types'
import { useSelector } from 'react-redux'
import { useCoreContext } from 'lib/core/hooks'

interface GetCartObjectDeps {
  cartId: string
  cart?: CartSlice
  productAttributeList: ProductAttributeList
  processors?: CartProcessors
  storeConfig: MagentoStoreConfig | undefined
}

type GetCartObject = (deps: GetCartObjectDeps) => undefined | {
  isFirstLoad: boolean
  isFetching: boolean
  isSuccess: boolean
  isFailure: boolean
  isCartException: boolean
  cartId: string
  count: number
  data?: CartData
}

const getCartObject: GetCartObject = ({
  cartId,
  cart,
  productAttributeList,
  storeConfig,
  processors
}): CartObject | undefined => {
  if (!cart) {
    return
  }

  const { isFetching, isSuccess, isFailure, isCartException, data } = cart
  const isFirstLoad = isFetching && !isSuccess && !Object.prototype.hasOwnProperty.call(data ?? {}, 'email')
  let cartData: CartData | undefined
  let count = 0

  if (data) {
    const {
      applied_coupon: appliedCoupon,
      applied_gift_cards: appliedGiftCards,
      available_payment_methods: availablePaymentMethods,
      email,
      items: cartItems,
      shipping_addresses: shippingAddresses,
      billing_address: billingAddressData,
      prices,
      done
    } = data

    const {
      applied_taxes: appliedTaxes,
      discount_amount: discount,
      grand_total: grandTotal,
      subtotal_excluding_tax: subtotalExcludingTax,
      subtotal_including_tax: subtotalIncludingTax
    } = prices || {}

    const items: CartItem[] = []
    const shippingAddress = getShippingAddress(shippingAddresses, processors)
    const billingAddress = getBillingAddress(billingAddressData)

    cartItems?.forEach(item => {
      const { id, product, quantity } = item
      const variantProduct = item?.variant_product
      const customizableOptions = item?.customizable_options
      const configurableOptions = item?.configurable_options
      const prices = item.prices

      let cartItem: CartItem = {
        id,
        prices,
        quantity,
        variantProduct: variantProduct ? normalizeProduct({
          storeConfig,
          productAttributeList,
          product: variantProduct
        }) : undefined,
        customizableOptions,
        configurableOptions,
        product: normalizeProduct({
          product,
          productAttributeList,
          storeConfig
        })
      }

      const cartItemProcessor = processors?.cartItemProcessor
      if (cartItemProcessor) {
        cartItem = produce(cartItem, draftCartItem => cartItemProcessor(draftCartItem, item))
      }

      count = count + quantity
      items.push(cartItem)
    })

    cartData = {
      appliedCoupon: appliedCoupon?.code ?? undefined,
      appliedGiftCards,
      availablePaymentMethods: availablePaymentMethods ?? [],
      billingAddress: billingAddress,
      currencyCode: cart.data?.prices.grand_total.currency ?? DEFAULT_CURRENCY_CODE,
      email: email ?? EMPTY_STRING,
      shippingAddress: shippingAddress,
      items: items,
      cartBought: !!done,
      prices: {
        appliedTaxes,
        discount,
        grandTotal,
        subtotalExcludingTax,
        subtotalIncludingTax
      }
    }
  }

  const cartProcessor = processors?.cartProcessor
  if (cartProcessor && cartData && data) {
    cartData = produce(cartData, draft => cartProcessor(draft, data))
  }

  return {
    isFirstLoad,
    isFetching,
    isSuccess,
    isCartException,
    isFailure,
    cartId,
    count,
    data: cartData
  }
}

const getShippingAddress = (shippingAddresses?: MagentoShippingCartAddress[], processors?: CartProcessors): ShippingAddress | undefined => {
  if (!(shippingAddresses?.length)) {
    return
  }
  const shippingAddressProcessor = processors?.shippingAddressProcessor
  const availableShippingMethodProcessor = processors?.availableShippingMethodProcessor
  const selectedShippingMethodProcessor = processors?.selectedShippingMethodProcessor

  const {
    customer_address_id: customerAddressId,
    firstname: firstName,
    lastname: lastName,
    company,
    street,
    city,
    postcode,
    region,
    country,
    telephone,
    available_shipping_methods: availableShippingMethods,
    selected_shipping_method: selectedShippingMethod
  } = shippingAddresses[0]

  const [street1, street2] = street

  let shippingAddress = {
    customerAddressId: customerAddressId ?? undefined,
    firstName: firstName ?? EMPTY_STRING,
    lastName: lastName ?? EMPTY_STRING,
    company: company ?? EMPTY_STRING,
    street: street1 ?? EMPTY_STRING,
    street2: street2 ?? EMPTY_STRING,
    city: city ?? EMPTY_STRING,
    postalCode: postcode ?? EMPTY_STRING,
    regionCode: region?.code ?? EMPTY_STRING,
    regionLabel: region?.label ?? EMPTY_STRING,
    countryCode: country?.code ?? EMPTY_STRING,
    phoneNumber: telephone ?? EMPTY_STRING,
    availableShippingMethods: availableShippingMethods?.filter(method => method.available).map(shippingMethod => { // TODO: use reduce
      return getAvailableShippingMethod(shippingMethod, availableShippingMethodProcessor)
    }) ?? [],
    selectedShippingMethod: getSelectedShippingMethod(selectedShippingMethod, selectedShippingMethodProcessor)
  }

  if (shippingAddressProcessor) {
    shippingAddress = produce(shippingAddress, draftShippingAddress => shippingAddressProcessor(draftShippingAddress, shippingAddresses[0]))
  }

  return shippingAddress
}

const getBillingAddress = (billingAddressData?: MagentoBillingCartAddress): BillingAddress | undefined => {
  if (!billingAddressData) {
    return
  }

  const {
    customer_address_id: customerAddressId,
    firstname: firstName,
    lastname: lastName,
    company,
    street,
    city,
    postcode,
    region,
    country,
    telephone
  } = billingAddressData

  const [street1, street2] = street

  return {
    customerAddressId: customerAddressId ?? undefined,
    firstName: firstName ?? EMPTY_STRING,
    lastName: lastName ?? EMPTY_STRING,
    company: company ?? EMPTY_STRING,
    street: street1 ?? EMPTY_STRING,
    street2: street2 ?? EMPTY_STRING,
    city: city ?? EMPTY_STRING,
    postalCode: postcode ?? EMPTY_STRING,
    regionCode: region?.code ?? EMPTY_STRING,
    regionLabel: region?.label ?? EMPTY_STRING,
    countryCode: country?.code ?? EMPTY_STRING,
    phoneNumber: telephone ?? EMPTY_STRING
  }
}

const getAvailableShippingMethod = (shippingMethod: MagentoAvailableShippingMethod, processor?: AvailableShippingMethodProcessor) => {
  const {
    // eslint-disable-next-line
    method_code,
    // eslint-disable-next-line
    method_title,
    // eslint-disable-next-line
    carrier_code,
    // eslint-disable-next-line
    amount,
    available,
    // eslint-disable-next-line
    base_amount,
    // eslint-disable-next-line
    carrier_title,
    // eslint-disable-next-line
    error_message,
    // eslint-disable-next-line
    price_excl_tax,
    // eslint-disable-next-line
    price_incl_tax
  } = shippingMethod

  let availableShippingMethod = { // TODO: create PWA type + camelCase.
    method_code,
    method_title,
    carrier_code,
    amount,
    available,
    base_amount,
    carrier_title,
    error_message,
    price_excl_tax,
    price_incl_tax
  }

  if (processor) {
    availableShippingMethod = produce(
      availableShippingMethod, draftAvailableShippingMethod => processor(draftAvailableShippingMethod, shippingMethod)
    )
  }

  return availableShippingMethod
}

const getSelectedShippingMethod = (shippingMethod?: MagentoSelectedShippingMethod, processor?: SelectedShippingMethodProcessor) => {
  if (!shippingMethod) {
    return
  }

  const {
    amount,
    // eslint-disable-next-line
    base_amount,
    // eslint-disable-next-line
    carrier_code,
    // eslint-disable-next-line
    carrier_title,
    // eslint-disable-next-line
    method_code,
    // eslint-disable-next-line
    method_title
  } = shippingMethod

  let selectedShippingMethod = { // TODO: create PWA type + camelCase.
    amount,
    base_amount,
    carrier_code,
    carrier_title,
    method_code,
    method_title
  }

  if (processor) {
    selectedShippingMethod = produce(
      selectedShippingMethod, draftSelectedShippingMethod => processor(draftSelectedShippingMethod, shippingMethod)
    )
  }

  return selectedShippingMethod
}

export const useAbandonedCart = (abandonedCartId: string, destinationCartId?: CartId) => {
  const productAttributeList = useSelector(getProductAttributeList)
  const { payload: storeConfig } = useSelector(getStoreConfigSlice)
  const { cartId } = useCart()
  const [mergeCarts, { data: mergedCarts, loading: mergingCarts }] = useMutation<{ mergeAbandonedCart: { id: string } }>(mergeAbandonedCarts, {
    variables: {
      sourceCartId: abandonedCartId,
      destinationCartId
    }
  })
  const { data, loading, error } = useQuery<{ getAbandonedCart: MagentoCart }>(abandonedCart, {
    variables: {
      cartId: abandonedCartId
    },
    skip: abandonedCartId === cartId
  })

  const { data: coreContext } = useCoreContext()
  const processors: CartProcessors = {
    cartProcessor: coreContext?.cart?.processor,
    cartItemProcessor: coreContext?.cartItems?.processor,
    shippingAddressProcessor: coreContext?.shippingAddress?.processor,
    availableShippingMethodProcessor: coreContext?.availableShippingMethod?.processor,
    selectedShippingMethodProcessor: coreContext?.selectedShippingMethod?.processor
  }

  const params: GetCartObjectDeps = {
    cartId: abandonedCartId,
    cart: {
      isFetching: loading,
      isSuccess: !!data,
      data: data?.getAbandonedCart,
      isFailure: !!error,
      isCartException: !!error
    },
    productAttributeList,
    processors,
    storeConfig
  }

  return {
    cartRaw: data,
    cart: data?.getAbandonedCart ? getCartObject(params) : undefined,
    isFetching: loading,
    mergeCarts,
    mergedCarts,
    mergingCarts
  }
}
